import React, { useRef } from 'react';
import CriteriaGroupCard from './CriteriaGroupCard';
import {
  appendChildAtPath,
  defaultCriteriaRoot,
  duplicateAtPath,
  getNode,
  moveOutOfGroupAtPath,
  removeAtPath,
  updateNodeAtPath,
  wrapInGroupAtPath,
} from './criteriaUtils';

/**
 * Root of the criteria editor. Owns tree mutations; rendering is delegated to group/condition components.
 * `onChange` must accept a functional updater (React state setter) so same-tick edits compose.
 */
const CriteriaTree = ({ criteria, onChange }) => {
  const root = criteria || defaultCriteriaRoot();
  // Path key of the condition added most recently, so its column picker opens automatically once.
  const newPathsRef = useRef(new Set());

  const apply = (fn) => onChange((prev) => fn(prev || defaultCriteriaRoot()));

  const handleChange = (path, updater) => apply((prev) => updateNodeAtPath(prev, path, updater));
  const handleRemove = (path) => apply((prev) => removeAtPath(prev, path));
  const handleAppend = (path, child) => {
    if (child.kind === 'condition') {
      const parent = getNode(root, path);
      const idx = (parent?.children || []).length;
      newPathsRef.current = new Set([[...path, idx].join('-')]);
    }
    apply((prev) => appendChildAtPath(prev, path, child));
  };
  const handleDuplicate = (path) => apply((prev) => duplicateAtPath(prev, path));
  const handleWrap = (path) =>
    apply((prev) => {
      const parent = getNode(prev, path.slice(0, -1));
      return wrapInGroupAtPath(prev, path, parent?.op === 'OR' ? 'AND' : 'OR');
    });
  const handleMoveOut = (path) => apply((prev) => moveOutOfGroupAtPath(prev, path));

  return (
    <CriteriaGroupCard
      node={root}
      path={[]}
      onChange={handleChange}
      onRemove={handleRemove}
      onAppend={handleAppend}
      onDuplicate={handleDuplicate}
      onWrap={handleWrap}
      onMoveOut={handleMoveOut}
      newPaths={newPathsRef.current}
    />
  );
};

export default CriteriaTree;
